import { FirestoreService } from "../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../api/firebase/references";
import { updateCigBoxNoteNewEndpoint } from "../api/postCall/updateCigBoxNote";
import { AllCommentsProps } from "../data-model/busniessOfficeNote";
import { UpdateCigBoxNoteNew } from "../data-model/updateCigBoxNoteNew";
import { ScreenNamesForBigQuery } from "../enums/eventsData";
import { EventsName } from "../enums/eventsName";
import { apiWorkerManager } from "../workerManagers/workerManager";
import { getCommentsToWriteFromAllComments } from "./postCommentHelper";

/**
 * Parameters required for saving CIG box note changes.
 * @interface SaveCigBoxNoteParams
 * @property {AllCommentsProps[]} allCommentsForUI - Array of comment groups containing notes to write back.
 * @property {string} patientMrn - The patient's Medical Record Number.
 * @property {string} drugName - The name of the drug associated with the order.
 * @property {string} orderId - The unique identifier of the order.
 * @property {string} org_id - The organization ID.
 * @property {string} bo_value - The business office status value.
 * @property {string} userEmail - The email of the user performing the action.
 * @property {string} userId - The ID of the user performing the action.
 * @property {string} payerName - The name of the payer/insurance.
 */
export interface SaveCigBoxNoteParams {
  allCommentsForUI: AllCommentsProps[];
  patientMrn: string;
  drugName: string;
  orderId: string;
  org_id: string;
  bo_value: string;
  userEmail: string;
  userId: string;
  payerName: string;
}

/**
 * Result object returned after attempting to save CIG box note changes.
 * @interface SaveCigBoxNoteResult
 * @property {boolean} success - Indicates whether the save operation was successful.
 * @property {string} [error] - Error message if the operation failed; undefined on success.
 */
export interface SaveCigBoxNoteResult {
  success: boolean;
  error?: string;
}

/**
 * Saves CIG box note changes by extracting FINAL-type comments, preparing a payload,
 * logging the event to BigQuery, and making an API call to update the notes.
 *
 * @async
 * @param {SaveCigBoxNoteParams} params - Object containing all required parameters for saving changes.
 * @param {AllCommentsProps[]} params.allCommentsForUI - Array of comment groups to process.
 * @param {string} params.patientMrn - The patient's Medical Record Number.
 * @param {string} params.drugName - The drug name associated with the order.
 * @param {string} params.orderId - The unique order identifier.
 * @param {string} params.org_id - The organization ID.
 * @param {string} params.bo_value - The business office status value.
 * @param {string} params.userEmail - Email of the user performing the action.
 * @param {string} params.userId - ID of the user performing the action.
 * @param {string} params.payerName - The payer/insurance name.
 * @returns {Promise<SaveCigBoxNoteResult>} Result object with success status and optional error message.
 * @example
 * const result = await saveCigBoxNoteChanges({
 *   allCommentsForUI: comments,
 *   patientMrn: "12345",
 *   drugName: "DrugA",
 *   orderId: "ORD-001",
 *   org_id: "org-123",
 *   bo_value: "approved",
 *   userEmail: "user@example.com",
 *   userId: "user-123",
 *   payerName: "InsuranceCo",
 * });
 * if (result.success) {
 *   console.log("Notes saved successfully");
 * } else {
 *   console.error("Failed to save:", result.error);
 * }
 */
export const saveCigBoxNoteChanges = async (
  params: SaveCigBoxNoteParams,
): Promise<SaveCigBoxNoteResult> => {
  const {
    allCommentsForUI,
    patientMrn,
    drugName,
    orderId,
    org_id,
    bo_value,
    userEmail,
    userId,
    payerName,
  } = params;

  try {
    const allPostComments = getCommentsToWriteFromAllComments(allCommentsForUI);

    // Prepare payload
    const payload: UpdateCigBoxNoteNew = {
      write_back_reports:
        allPostComments && allPostComments.length > 0 ? allPostComments : [],
      patient_mrn: patientMrn,
      drug_name: drugName,
      identifier: orderId,
      org_id: org_id,
      bo_status: bo_value,
    };

    apiWorkerManager.enqueueAndProcess({
      patient_id: patientMrn,
      order_id: orderId,
      org_id: org_id,
      user_id: userId,
      user_email: userEmail,
      event_name: EventsName.WRITE_BACK_NOTES_INITIATED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
        patient_mrn: patientMrn,
        drug_name: drugName,
        payer_name: payerName,
      },
    });

    await updateCigBoxNoteNewEndpoint(payload);

    // Sync bo_status to Firestore so it's available in single order view
    if (bo_value && orderId) {
      try {
        await FirestoreService.updateDocument(
          FirestoreCollectionReference.medicalPaOrders(),
          orderId,
          {
            "status.bo_status": bo_value,
          },
        );
      } catch (firestoreError) {
        console.error("Failed to sync bo_status to Firestore:", firestoreError);
        // Don't fail the entire operation if Firestore sync fails
      }
    }

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};
